import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Building2, Pencil, X,
  Phone, Mail, Globe, MapPin,
  CheckCircle2, Ban, Plus, Trash2, DoorOpen, Upload,
} from 'lucide-react';
import api from '@/lib/axios';
import { getInitials, cn } from '@/lib/utils';
import { useAuthStore, ROL } from '@/store/auth.store';
import { LogoCropModal } from '@/components/ui/LogoCropModal';
import { getErrorMessage } from '@/lib/errors';
import { presetParaTipo, TIPOS_CLINICA, COLOR_TIPO } from '@/lib/perfilClinicaPresets';

// ── Tipos ─────────────────────────────────────────────────────────────────────

type DiaSemana = 'lunes' | 'martes' | 'miercoles' | 'jueves' | 'viernes' | 'sabado' | 'domingo';

interface SlotApi {
  id: string;
  diaSemana: DiaSemana;
  horaInicio: string;
  horaFin: string;
  esBloqueo: boolean;
  motivo: string | null;
}

interface SlotEditable {
  localId: string;
  diaSemana: DiaSemana;
  horaInicio: string;
  horaFin: string;
  esBloqueo: boolean;
  motivo: string;
}

type HorarioEditable = Record<DiaSemana, SlotEditable[]>;

// ── Constantes ────────────────────────────────────────────────────────────────

const DIAS: DiaSemana[] = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
const DIAS_LABEL: Record<DiaSemana, string> = {
  lunes: 'Lunes', martes: 'Martes', miercoles: 'Miércoles',
  jueves: 'Jueves', viernes: 'Viernes', sabado: 'Sábado', domingo: 'Domingo',
};
const MOTIVOS = [
  { value: 'comida',       label: 'Comida / descanso' },
  { value: 'cierre',       label: 'Cierre anticipado' },
  { value: 'junta',        label: 'Junta / reunión' },
  { value: 'capacitacion', label: 'Capacitación' },
  { value: 'vacaciones',   label: 'Vacaciones / festivo' },
  { value: 'otro',         label: 'Otro' },
];
const TIPOS = TIPOS_CLINICA;

// México abarca varias zonas horarias — el horario de atención de la clínica
// (ClinicaHorario) se interpreta con este timezone, no con uno fijo para todo el país.
const TIMEZONES_MX = [
  { value: 'America/Tijuana',     label: 'Tijuana / Noroeste (Baja California)' },
  { value: 'America/Mazatlan',    label: 'Mazatlán / Pacífico (BCS, Sinaloa, Nayarit, Sonora)' },
  { value: 'America/Mexico_City', label: 'Ciudad de México / Centro' },
  { value: 'America/Cancun',      label: 'Cancún / Sureste (Quintana Roo)' },
];

// ── Utilidades ────────────────────────────────────────────────────────────────

let _lid = 0;
const newLocalId = () => `l${++_lid}`;
const toHHMM = (t: string) => (t ?? '').slice(0, 5);

function emptyHorario(): HorarioEditable {
  return { lunes: [], martes: [], miercoles: [], jueves: [], viernes: [], sabado: [], domingo: [] };
}
function slotsFromApi(list: SlotApi[]): HorarioEditable {
  const h = emptyHorario();
  list.forEach(s => h[s.diaSemana].push({
    localId: newLocalId(), diaSemana: s.diaSemana,
    horaInicio: toHHMM(s.horaInicio), horaFin: toHHMM(s.horaFin),
    esBloqueo: s.esBloqueo, motivo: s.motivo ?? '',
  }));
  return h;
}
function slotsToApi(h: HorarioEditable) {
  return DIAS.flatMap(d => h[d].map(({ localId: _, ...rest }) => rest));
}

// ── Estilos ───────────────────────────────────────────────────────────────────

const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';
const labelCls = 'block text-xs font-medium text-text-secondary mb-1';
const timeCls  = 'px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 tabular-nums';

function SectionCard({ children }: { children: React.ReactNode }) {
  return <div className="bg-white rounded-xl border border-gray-100 p-5">{children}</div>;
}
function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-4">{children}</h3>;
}

// ── Horario vista ─────────────────────────────────────────────────────────────

function HorarioView({ slots }: { slots: SlotApi[] }) {
  if (!slots.length) return <p className="text-sm text-text-secondary italic">Sin horario registrado</p>;

  const byDay = Object.fromEntries(DIAS.map(d => [d, slots.filter(s => s.diaSemana === d)]));
  return (
    <div className="space-y-2">
      {DIAS.map(dia => {
        const sl = byDay[dia];
        return (
          <div key={dia} className="flex items-start gap-3">
            <span className="text-sm text-text-secondary w-24 flex-shrink-0 pt-0.5">{DIAS_LABEL[dia]}</span>
            {!sl.length ? (
              <span className="text-xs text-text-secondary italic pt-0.5">Cerrado</span>
            ) : (
              <div className="flex flex-col gap-1">
                {sl.sort((a, b) => a.horaInicio.localeCompare(b.horaInicio)).map(s => (
                  <div key={s.id} className="flex items-center gap-2">
                    {s.esBloqueo
                      ? <Ban size={12} className="text-error-500 flex-shrink-0" />
                      : <CheckCircle2 size={12} className="text-success-600 flex-shrink-0" />}
                    <span className={`text-sm tabular-nums ${s.esBloqueo ? 'text-text-secondary line-through' : 'text-text-primary'}`}>
                      {toHHMM(s.horaInicio)} – {toHHMM(s.horaFin)}
                    </span>
                    {s.esBloqueo && s.motivo && (
                      <span className="text-xs text-text-secondary">
                        ({MOTIVOS.find(m => m.value === s.motivo)?.label ?? s.motivo})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Horario editor ────────────────────────────────────────────────────────────

function HorarioEdit({ value, onChange }: { value: HorarioEditable; onChange: (v: HorarioEditable) => void }) {
  function addSlot(dia: DiaSemana) {
    const ex = value[dia];
    const lastFin = ex[ex.length - 1]?.horaFin ?? '09:00';
    onChange({ ...value, [dia]: [...ex, { localId: newLocalId(), diaSemana: dia, horaInicio: lastFin, horaFin: lastFin < '17:00' ? '18:00' : '20:00', esBloqueo: false, motivo: '' }] });
  }
  function remove(dia: DiaSemana, lid: string) {
    onChange({ ...value, [dia]: value[dia].filter(s => s.localId !== lid) });
  }
  function patch(dia: DiaSemana, lid: string, p: Partial<SlotEditable>) {
    onChange({ ...value, [dia]: value[dia].map(s => s.localId === lid ? { ...s, ...p } : s) });
  }
  return (
    <div className="space-y-5">
      {DIAS.map(dia => (
        <div key={dia}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-primary">{DIAS_LABEL[dia]}</span>
            <button type="button" onClick={() => addSlot(dia)} className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium">
              <Plus size={12} /> Agregar franja
            </button>
          </div>
          {!value[dia].length ? (
            <p className="text-xs text-text-secondary italic">Cerrado — sin franjas</p>
          ) : (
            <div className="space-y-2">
              {value[dia].map(slot => (
                <div key={slot.localId} className="flex items-center gap-2 flex-wrap">
                  <input type="time" value={slot.horaInicio} onChange={e => patch(dia, slot.localId, { horaInicio: e.target.value })} className={timeCls} />
                  <span className="text-text-secondary">–</span>
                  <input type="time" value={slot.horaFin} onChange={e => patch(dia, slot.localId, { horaFin: e.target.value })} className={timeCls} />
                  <select
                    value={slot.esBloqueo ? 'bloqueado' : 'disponible'}
                    onChange={e => patch(dia, slot.localId, { esBloqueo: e.target.value === 'bloqueado', motivo: '' })}
                    className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  >
                    <option value="disponible">Disponible</option>
                    <option value="bloqueado">Bloqueado</option>
                  </select>
                  {slot.esBloqueo && (
                    <select value={slot.motivo} onChange={e => patch(dia, slot.localId, { motivo: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300">
                      <option value="">Motivo…</option>
                      {MOTIVOS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  )}
                  <button type="button" onClick={() => remove(dia, slot.localId)} className="p-1 text-gray-400 hover:text-error-500 rounded transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Formulario inline de consultorio (a nivel módulo para evitar remount) ─────

interface ConsultorioFormFields {
  nombre: string;
  descripcion: string;
  responsableId: string;
  activo: boolean;
}

interface ConsultorioInlineFormProps {
  isNew: boolean;
  ef: ConsultorioFormFields;
  onChange: (k: keyof ConsultorioFormFields, v: string | boolean) => void;
  medicos: any[];
  onSave: (e: React.FormEvent) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function ConsultorioInlineForm({ isNew, ef, onChange, medicos, onSave, onCancel, isSaving }: ConsultorioInlineFormProps) {
  const field = (k: keyof ConsultorioFormFields) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => onChange(k, e.target.value);

  return (
    <form onSubmit={onSave} className={`rounded-xl border p-3 space-y-2 ${isNew ? 'bg-primary-50 border-primary-100' : 'bg-gray-50 border-gray-200'}`}>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>Nombre <span className="text-red-500">*</span></label>
          <input required autoFocus value={ef.nombre} onChange={field('nombre')} className={inputCls} placeholder="Consultorio 2" />
        </div>
        <div>
          <label className={labelCls}>Responsable</label>
          <select value={ef.responsableId} onChange={field('responsableId')} className={inputCls}>
            <option value="">Sin asignar</option>
            {medicos.map((m: any) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className={labelCls}>Descripción</label>
          <input value={ef.descripcion} onChange={field('descripcion')} className={inputCls} placeholder="Opcional" />
        </div>
        {!isNew && (
          <label className="flex items-center gap-2 cursor-pointer pb-2">
            <input type="checkbox" checked={ef.activo} onChange={e => onChange('activo', e.target.checked)} className="w-4 h-4 rounded accent-primary-500" />
            <span className="text-sm text-text-primary">Activo</span>
          </label>
        )}
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-text-primary hover:bg-white rounded-lg transition-colors">Cancelar</button>
        <button type="submit" disabled={isSaving} className="px-3 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60">
          {isSaving ? 'Guardando...' : isNew ? 'Crear consultorio' : 'Guardar'}
        </button>
      </div>
    </form>
  );
}

// ── Consultorios (siempre interactivo) ────────────────────────────────────────

function ConsultoriosSection({ medicos }: { medicos: any[] }) {
  const qc = useQueryClient();
  const { data: consultorios = [], isLoading } = useQuery({
    queryKey: ['clinica-consultorios'],
    queryFn: () => api.get('/api/clinica/consultorios').then(r => r.data),
  });

  const [editing, setEditing] = useState<string | null>(null);
  const [ef, setEf] = useState<ConsultorioFormFields>({ nombre: '', descripcion: '', responsableId: '', activo: true });

  const inv = () => qc.invalidateQueries({ queryKey: ['clinica-consultorios'] });
  const createMut = useMutation({
    mutationFn: (dto: any) => api.post('/api/clinica/consultorios', dto).then(r => r.data),
    onSuccess: () => { inv(); setEditing(null); toast.success('Consultorio creado'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear consultorio')),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: any }) => api.patch(`/api/clinica/consultorios/${id}`, dto).then(r => r.data),
    onSuccess: () => { inv(); setEditing(null); toast.success('Consultorio actualizado'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar consultorio')),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/api/clinica/consultorios/${id}`).then(r => r.data),
    onSuccess: () => { inv(); toast.success('Consultorio eliminado'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar consultorio')),
  });

  function startNew() { setEf({ nombre: '', descripcion: '', responsableId: '', activo: true }); setEditing('new'); }
  function startEdit(c: any) { setEf({ nombre: c.nombre, descripcion: c.descripcion ?? '', responsableId: c.responsableId ?? '', activo: c.activo }); setEditing(c.id); }

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const dto = { nombre: ef.nombre, descripcion: ef.descripcion || null, responsableId: ef.responsableId || null, activo: ef.activo };
    if (editing === 'new') createMut.mutate(dto);
    else if (editing) updateMut.mutate({ id: editing, dto });
  }

  function handleChange(k: keyof ConsultorioFormFields, v: string | boolean) {
    setEf(p => ({ ...p, [k]: v }));
  }

  const isSaving = createMut.isPending || updateMut.isPending;

  return (
    <SectionCard>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Consultorios</SectionTitle>
        {editing !== 'new' && (
          <button onClick={startNew} className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium">
            <Plus size={12} /> Agregar
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-text-secondary">Cargando...</p>
      ) : (
        <div className="space-y-2">
          {editing === 'new' && (
            <ConsultorioInlineForm isNew ef={ef} onChange={handleChange} medicos={medicos} onSave={handleSave} onCancel={() => setEditing(null)} isSaving={isSaving} />
          )}
          {consultorios.length === 0 && editing !== 'new' && (
            <p className="text-sm text-text-secondary italic">Sin consultorios registrados</p>
          )}
          {consultorios.map((c: any) => (
            <div key={c.id}>
              {editing === c.id ? (
                <ConsultorioInlineForm isNew={false} ef={ef} onChange={handleChange} medicos={medicos} onSave={handleSave} onCancel={() => setEditing(null)} isSaving={isSaving} />
              ) : (
                <div className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${c.activo ? 'border-gray-100 hover:border-gray-200' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                  <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <DoorOpen size={16} className="text-primary-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary">{c.nombre}</p>
                    <p className="text-xs text-text-secondary">{c.responsable?.nombre ?? 'Sin responsable'}</p>
                    {c.descripcion && <p className="text-xs text-text-secondary mt-0.5 truncate">{c.descripcion}</p>}
                  </div>
                  {!c.activo && <span className="text-xs text-gray-400 italic flex-shrink-0">Inactivo</span>}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => startEdit(c)} className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => deleteMut.mutate(c.id)} disabled={deleteMut.isPending} className="p-1.5 text-gray-400 hover:text-error-500 hover:bg-error-50 rounded-lg transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────

export function ClinicaPage() {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<any>({});
  const [horarioEdits, setHorarioEdits] = useState<HorarioEditable>(emptyHorario);

  const qc = useQueryClient();
  const { usuario } = useAuthStore();
  const canWriteLogo = [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO_ADMIN].includes(usuario?.rolId as any);

  // ── Logo de la clínica ─────────────────────────────────────────────────────
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoCropSrc,   setLogoCropSrc]   = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  function handleLogoFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setLogoCropSrc(ev.target?.result as string);
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  async function handleLogoCropSave(blob: Blob) {
    setLogoCropSrc(null);
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'logo.png');
      const { data: uploadData } = await api.post(
        `/api/storage/upload?entityId=${clinica?.id}&existingUrl=${encodeURIComponent(clinica?.logoUrl ?? '')}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      await api.patch('/api/clinica', { logoUrl: uploadData.url });
      qc.invalidateQueries({ queryKey: ['clinica'] });
      toast.success('Logo actualizado');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al subir el logo'));
    } finally {
      setUploadingLogo(false);
    }
  }

  async function handleLogoDelete() {
    try {
      await api.patch('/api/clinica', { logoUrl: null });
      qc.invalidateQueries({ queryKey: ['clinica'] });
      toast.success('Logo eliminado');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al eliminar el logo'));
    }
  }

  const { data: clinica, isLoading } = useQuery({
    queryKey: ['clinica'],
    queryFn: () => api.get('/api/clinica').then(r => r.data),
  });

  const { data: medicos = [] } = useQuery({
    queryKey: ['medicos'],
    queryFn: () => api.get('/api/medicos').then(r => r.data),
  });

  const { data: horarios = [] } = useQuery<SlotApi[]>({
    queryKey: ['clinica-horarios'],
    queryFn: () => api.get('/api/clinica/horarios').then(r => r.data),
  });

  function startEdit() {
    if (!clinica) return;
    setForm({
      nombre:              clinica.nombre ?? '',
      tipoClinica:         clinica.tipoClinica ?? 'medica_general',
      telefono:            clinica.telefono ?? '',
      correo:              clinica.correo ?? '',
      sitioWeb:            clinica.sitioWeb ?? '',
      direccion:           clinica.direccion ?? '',
      medicoResponsableId: clinica.medicoResponsableId ?? '',
      razonSocial:         clinica.razonSocial ?? '',
      rfc:                 clinica.rfc ?? '',
      claveClues:          clinica.claveClues ?? '',
      timezone:            clinica.timezone ?? 'America/Mexico_City',
    });
    setHorarioEdits(slotsFromApi(horarios));
    setIsEditing(true);
  }

  const updateMut = useMutation({
    mutationFn: (data: any) => api.patch('/api/clinica', data).then(r => r.data),
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar la clínica')),
  });
  const replaceHorariosMut = useMutation({
    mutationFn: (slots: any[]) => api.put('/api/clinica/horarios', { slots }).then(r => r.data),
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar el horario')),
  });
  // clinica.tipoClinica es la única fuente de verdad para el tipo de clínica; al cambiarlo
  // sincronizamos configuracion.perfilTipo + los presets de módulos/antecedentes (ver Ajustes › Sistema).
  const syncPerfilMut = useMutation({
    mutationFn: (tipo: string) => {
      const preset = presetParaTipo(tipo);
      return api.put('/api/configuracion', {
        perfilTipo: tipo,
        modulosHabilitadosJson: preset.modulosHabilitadosJson,
        configuracionJson: { antecedentesSecciones: preset.antecedentesSecciones },
      }).then(r => r.data);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al sincronizar los módulos con el nuevo tipo de clínica')),
  });

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      ...form,
      rfc:                 form.rfc ? form.rfc.toUpperCase() : null,
      medicoResponsableId: form.medicoResponsableId || null,
      sitioWeb:            form.sitioWeb || null,
      correo:              form.correo || null,
      telefono:            form.telefono || null,
      razonSocial:         form.razonSocial || null,
      claveClues:          form.claveClues || null,
    };
    const tipoCambio = form.tipoClinica !== clinica?.tipoClinica;
    try {
      await Promise.all([
        updateMut.mutateAsync(payload),
        replaceHorariosMut.mutateAsync(slotsToApi(horarioEdits)),
        ...(tipoCambio ? [syncPerfilMut.mutateAsync(form.tipoClinica)] : []),
      ]);
      qc.invalidateQueries({ queryKey: ['clinica'] });
      qc.invalidateQueries({ queryKey: ['clinica-horarios'] });
      if (tipoCambio) qc.invalidateQueries({ queryKey: ['configuracion'] });
      toast.success('Clínica actualizada');
      setIsEditing(false);
    } catch {
      // errors handled by mutations
    }
  }

  const set = (field: string, val: any) => setForm((p: any) => ({ ...p, [field]: val }));
  const isSaving = updateMut.isPending || replaceHorariosMut.isPending || syncPerfilMut.isPending;

  const medicoResponsable = medicos.find((m: any) => m.id === clinica?.medicoResponsableId);
  const tipoLabel = TIPOS.find(t => t.value === clinica?.tipoClinica)?.label;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-100 rounded animate-pulse" />
        <div className="h-64 bg-gray-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!clinica) {
    return (
      <div className="text-center py-20">
        <p className="text-text-secondary">No se encontró información de la clínica</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {logoCropSrc && (
        <LogoCropModal
          src={logoCropSrc}
          title="Recortar logo de la clínica"
          onSave={handleLogoCropSave}
          onCancel={() => setLogoCropSrc(null)}
        />
      )}

      {/* ── Encabezado ── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
            <Building2 size={20} className="text-primary-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-text-primary">
              {isEditing ? (form.nombre || 'Editar clínica') : clinica.nombre}
            </h2>
            {!isEditing && tipoLabel && (
              <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${COLOR_TIPO[clinica.tipoClinica] || 'bg-gray-100 text-gray-600'}`}>
                {tipoLabel}
              </span>
            )}
          </div>
        </div>

        {!isEditing && (
          <button
            onClick={startEdit}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-primary-600 border border-primary-200 hover:bg-primary-50 rounded-lg transition-colors flex-shrink-0"
          >
            <Pencil size={14} /> Editar
          </button>
        )}
      </div>

      {/* ── Logo ── */}
      {canWriteLogo && (
        <SectionCard>
          <div className="flex items-center gap-2 mb-1">
            <Building2 size={15} className="text-primary-500" />
            <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Logo de la clínica</h3>
          </div>
          <p className="text-xs text-text-secondary mb-4">
            Aparece en recetas, órdenes, solicitudes de laboratorio, presupuestos y planes de tratamiento.
          </p>
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {clinica?.logoUrl ? (
                <img
                  src={clinica.logoUrl}
                  alt="Logo clínica"
                  className="w-full h-full object-contain p-1"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <Building2 size={24} className="text-gray-300" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                disabled={uploadingLogo}
                onClick={() => logoInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {uploadingLogo
                  ? <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  : <Upload size={13} />
                }
                {clinica?.logoUrl ? 'Cambiar logo' : 'Subir logo'}
              </button>
              {clinica?.logoUrl && (
                <button
                  type="button"
                  onClick={handleLogoDelete}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 size={13} />
                  Eliminar
                </button>
              )}
            </div>
          </div>
          <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleLogoFileChange} />
        </SectionCard>
      )}

      {/* ── MODO EDICIÓN ── */}
      {isEditing && (
        <form onSubmit={handleSave} className="space-y-4">

          {/* Banner de edición */}
          <div className="flex items-center justify-between px-4 py-3 bg-primary-50 border border-primary-100 rounded-xl">
            <p className="text-sm text-primary-700 font-medium">Modo edición</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setIsEditing(false)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-text-primary bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <X size={14} /> Cancelar
              </button>
              <button type="submit" disabled={isSaving} className="px-4 py-1.5 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60">
                {isSaving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Identificación + Contacto + Ubicación */}
            <div className="space-y-4">
              <SectionCard>
                <SectionTitle>Identificación</SectionTitle>
                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>Nombre <span className="text-red-500">*</span></label>
                    <input required value={form.nombre} onChange={e => set('nombre', e.target.value)} className={inputCls} placeholder="Clínica Dental Sonrisa" />
                  </div>
                  <div>
                    <label className={labelCls}>Tipo de clínica</label>
                    <p className="text-xs text-text-secondary mb-2">
                      Al cambiarlo se aplican los módulos y secciones de antecedentes sugeridos para ese perfil
                      (ajustables después en Ajustes › Sistema).
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {TIPOS.map(t => {
                        const Icon = t.icon;
                        const selected = form.tipoClinica === t.value;
                        return (
                          <button
                            key={t.value}
                            type="button"
                            onClick={() => set('tipoClinica', t.value)}
                            className={cn(
                              'flex items-start gap-2.5 p-2.5 rounded-xl border text-left transition-all',
                              selected
                                ? 'bg-primary-50 border-primary-300 ring-1 ring-primary-200'
                                : 'border-gray-200 hover:bg-gray-50',
                            )}
                          >
                            <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0', t.color)}>
                              <Icon size={14} />
                            </div>
                            <div className="min-w-0">
                              <p className={cn('text-xs font-semibold', selected ? 'text-primary-700' : 'text-text-primary')}>
                                {t.label}
                              </p>
                              <p className="text-[11px] text-text-secondary mt-0.5 leading-snug">{t.description}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Zona horaria</label>
                    <select value={form.timezone} onChange={e => set('timezone', e.target.value)} className={inputCls}>
                      {TIMEZONES_MX.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                    </select>
                    <p className="text-xs text-text-secondary mt-1">
                      Se usa para interpretar el horario de atención y la disponibilidad de la agenda.
                    </p>
                  </div>
                </div>
              </SectionCard>

              <SectionCard>
                <SectionTitle>Contacto</SectionTitle>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Teléfono</label>
                      <input value={form.telefono} onChange={e => set('telefono', e.target.value)} className={inputCls} placeholder="55 1234 5678" />
                    </div>
                    <div>
                      <label className={labelCls}>Correo electrónico</label>
                      <input type="email" value={form.correo} onChange={e => set('correo', e.target.value)} className={inputCls} placeholder="info@clinica.com" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Sitio web <span className="font-normal">(opcional)</span></label>
                    <input value={form.sitioWeb} onChange={e => set('sitioWeb', e.target.value)} className={inputCls} placeholder="https://www.clinica.com" />
                  </div>
                </div>
              </SectionCard>

              <SectionCard>
                <SectionTitle>Ubicación</SectionTitle>
                <div>
                  <label className={labelCls}>Dirección</label>
                  <textarea value={form.direccion} onChange={e => set('direccion', e.target.value)} rows={2} className={`${inputCls} resize-none`} placeholder="Calle, colonia, ciudad, CP" />
                </div>
              </SectionCard>
            </div>

            <div className="space-y-4">              
            {/* Horario */}
            <SectionCard>
              <SectionTitle>Horario de atención</SectionTitle>
              <HorarioEdit value={horarioEdits} onChange={setHorarioEdits} />
            </SectionCard>

              <SectionCard>
                <SectionTitle>Médico responsable</SectionTitle>
                <select value={form.medicoResponsableId} onChange={e => set('medicoResponsableId', e.target.value)} className={inputCls}>
                  <option value="">Sin médico responsable</option>
                  {medicos.map((m: any) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
              </SectionCard>

              <SectionCard>
                <SectionTitle>Datos fiscales <span className="font-normal normal-case text-text-secondary">(opcional)</span></SectionTitle>
                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>Razón social</label>
                    <input value={form.razonSocial} onChange={e => set('razonSocial', e.target.value)} className={inputCls} placeholder="Clínica Sonrisa SA de CV" />
                  </div>
                  <div>
                    <label className={labelCls}>RFC</label>
                    <input value={form.rfc} onChange={e => set('rfc', e.target.value.toUpperCase())} className={`${inputCls} uppercase`} placeholder="XAXX010101000" maxLength={13} />
                  </div>
                  <div>
                    <label className={labelCls}>Clave CLUES</label>
                    <input value={form.claveClues} onChange={e => set('claveClues', e.target.value.toUpperCase())} className={`${inputCls} uppercase`} placeholder="ej. NLSSA001234" maxLength={50} />
                    <p className="text-xs text-text-secondary mt-1">
                      Dato obligatorio ante COFEPRIS para la emisión de recetas digitales.
                    </p>
                  </div>
                </div>
              </SectionCard>
            </div>
          </div>

          {/* Botones al final también */}
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setIsEditing(false)} className="px-5 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={isSaving} className="px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60">
              {isSaving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      )}

      {/* ── MODO VISTA ── */}
      {!isEditing && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Columna izquierda */}
          <div className="space-y-4">

            <SectionCard>
              <SectionTitle>Contacto</SectionTitle>
              {clinica.telefono || clinica.correo || clinica.sitioWeb ? (
                <div className="space-y-3">
                  {clinica.telefono && (
                    <div className="flex items-center gap-2.5">
                      <Phone size={15} className="text-text-secondary flex-shrink-0" />
                      <a href={`tel:${clinica.telefono}`} className="text-sm text-text-primary hover:text-primary-600 transition-colors">{clinica.telefono}</a>
                    </div>
                  )}
                  {clinica.correo && (
                    <div className="flex items-center gap-2.5">
                      <Mail size={15} className="text-text-secondary flex-shrink-0" />
                      <a href={`mailto:${clinica.correo}`} className="text-sm text-text-primary hover:text-primary-600 transition-colors">{clinica.correo}</a>
                    </div>
                  )}
                  {clinica.sitioWeb && (
                    <div className="flex items-center gap-2.5">
                      <Globe size={15} className="text-text-secondary flex-shrink-0" />
                      <a href={clinica.sitioWeb} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 hover:underline">{clinica.sitioWeb}</a>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-text-secondary italic">Sin información de contacto</p>
              )}
            </SectionCard>

            <SectionCard>
              <SectionTitle>Ubicación</SectionTitle>
              {clinica.direccion ? (
                <div className="flex items-start gap-2.5">
                  <MapPin size={15} className="text-text-secondary mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-text-primary">{clinica.direccion}</p>
                </div>
              ) : (
                <p className="text-sm text-text-secondary italic">Sin dirección registrada</p>
              )}
            </SectionCard>

            <SectionCard>
              <SectionTitle>Médico responsable</SectionTitle>
              {medicoResponsable ? (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold flex-shrink-0">
                    {getInitials(medicoResponsable.nombre)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">{medicoResponsable.nombre}</p>
                    {medicoResponsable.especialidad && (
                      <p className="text-xs text-text-secondary">{medicoResponsable.especialidad.nombre}</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-text-secondary italic">Sin médico responsable asignado</p>
              )}
            </SectionCard>

            {(clinica.razonSocial || clinica.rfc || clinica.claveClues) && (
              <SectionCard>
                <SectionTitle>Datos fiscales</SectionTitle>
                <div className="space-y-3">
                  {clinica.razonSocial && (
                    <div>
                      <p className="text-xs text-text-secondary mb-0.5">Razón social</p>
                      <p className="text-sm text-text-primary">{clinica.razonSocial}</p>
                    </div>
                  )}
                  {clinica.rfc && (
                    <div>
                      <p className="text-xs text-text-secondary mb-0.5">RFC</p>
                      <p className="text-sm text-text-primary font-mono">{clinica.rfc}</p>
                    </div>
                  )}
                  {clinica.claveClues && (
                    <div>
                      <p className="text-xs text-text-secondary mb-0.5">Clave CLUES</p>
                      <p className="text-sm text-text-primary font-mono">{clinica.claveClues}</p>
                    </div>
                  )}
                </div>
              </SectionCard>
            )}
          </div>

          {/* Columna derecha */}
          <div className="space-y-4">
            <SectionCard>
              <div className="flex items-center justify-between mb-4">
                <SectionTitle>Horario de atención</SectionTitle>
                <span className="text-xs text-text-secondary">
                  {TIMEZONES_MX.find(tz => tz.value === (clinica.timezone || 'America/Mexico_City'))?.label ?? clinica.timezone}
                </span>
              </div>
              <HorarioView slots={horarios} />
            </SectionCard>

            <ConsultoriosSection medicos={medicos} />
          </div>

        </div>
      )}
    </div>
  );
}
